import { create } from 'zustand';
import { GameType, GamePhase, SpinnerArm } from '@shark-games/shared';

interface Player {
  sessionId: string;
  userId: string;
  displayName: string;
  avatarUrl?: string;
  seatIndex: number;
  team?: string;
  isConnected: boolean;
  isReady: boolean;
  score: number;
  hand: string[];
}

interface OpenEnds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface ValidPlay {
  dominoId: string;
  arms: SpinnerArm[];
}

export interface TileInfo {
  dominoId: string;
  connectingEnd: number;
  openEnd: number;
}

interface GameState {
  roomId: string | null;
  gameType: GameType | null;
  phase: GamePhase;
  players: Map<string, Player>;
  currentTurnPlayerId: string | null;
  turnTimer: number;
  mySessionId: string | null;

  // Dominoes specific - legacy
  boardTiles: string[];
  leftEnd: number;
  rightEnd: number;
  boneyardCount: number;

  // Dominoes spinner state
  hasSpinner: boolean;
  spinnerValue: number;
  spinnerId: string;
  leftArm: TileInfo[];
  rightArm: TileInfo[];
  topArm: TileInfo[];
  bottomArm: TileInfo[];
  openEnds: OpenEnds;
  preSpinnerTiles: TileInfo[];
  validPlays: ValidPlay[];

  // Dominoes round/scoring state
  roundResult: { winnerId: string | null; isBlocked: boolean } | null;
  roundScores: Record<string, { score: number; handTotal: number }> | null;
  showRoundEndOverlay: boolean;
  scoreToWin: number;

  // Spades specific
  team1Score: number;
  team1Bags: number;
  team2Score: number;
  team2Bags: number;
  currentTrick: string[];
  spadesBroken: boolean;
  bids: Map<string, number>;
  tricksWon: Map<string, number>;
  roundNumber: number;
  leadSuit: string;
  team1Bid: number;
  team2Bid: number;
  team1Tricks: number;
  team2Tricks: number;
  validCards: string[];

  // Actions
  setRoomState: (state: Partial<GameState>) => void;
  setPlayer: (sessionId: string, player: Player) => void;
  removePlayer: (sessionId: string) => void;
  updatePlayer: (sessionId: string, updates: Partial<Player>) => void;
  reset: () => void;
}

const initialState = {
  roomId: null,
  gameType: null,
  phase: 'waiting' as GamePhase,
  players: new Map<string, Player>(),
  currentTurnPlayerId: null,
  turnTimer: 0,
  mySessionId: null,
  // Dominoes legacy
  boardTiles: [] as string[],
  leftEnd: -1,
  rightEnd: -1,
  boneyardCount: 0,
  // Dominoes round/scoring
  roundResult: null,
  roundScores: null,
  showRoundEndOverlay: false,
  scoreToWin: 150,
  // Dominoes spinner
  hasSpinner: false,
  spinnerValue: -1,
  spinnerId: '',
  leftArm: [] as TileInfo[],
  rightArm: [] as TileInfo[],
  topArm: [] as TileInfo[],
  bottomArm: [] as TileInfo[],
  openEnds: { left: -1, right: -1, top: -1, bottom: -1 } as OpenEnds,
  preSpinnerTiles: [] as TileInfo[],
  validPlays: [] as ValidPlay[],
  // Spades
  team1Score: 0,
  team1Bags: 0,
  team2Score: 0,
  team2Bags: 0,
  currentTrick: [] as string[],
  spadesBroken: false,
  bids: new Map<string, number>(),
  tricksWon: new Map<string, number>(),
  roundNumber: 1,
  leadSuit: '',
  team1Bid: 0,
  team2Bid: 0,
  team1Tricks: 0,
  team2Tricks: 0,
  validCards: [] as string[],
};

export const useGameStore = create<GameState>((set) => ({
  ...initialState,

  setRoomState: (state) => set((prev) => ({ ...prev, ...state })),

  setPlayer: (sessionId, player) =>
    set((state) => {
      const players = new Map(state.players);
      players.set(sessionId, player);
      return { players };
    }),

  removePlayer: (sessionId) =>
    set((state) => {
      const players = new Map(state.players);
      players.delete(sessionId);
      return { players };
    }),

  updatePlayer: (sessionId, updates) =>
    set((state) => {
      const players = new Map(state.players);
      const player = players.get(sessionId);
      if (player) {
        players.set(sessionId, { ...player, ...updates });
      }
      return { players };
    }),

  reset: () => set(initialState),
}));
